import { devWarn, devError, devInfo } from "@/lib/logger";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { TEST_LAUNCH_MODE, SUPPORT_EMAIL } from "@/lib/config";
import {
  getAlertPrefs, setAlertPrefs,
  getMyReferral, connectOnboard, connectStatus,
  exportUserData, deleteUserAccount,
  pushStatus, pushTest, pushDigestSendNow,
} from "@/lib/api";
import { pushSupported, currentPermission, enablePush, disablePush, getCurrentSubscription } from "@/lib/push";
import { fetchMe } from "@/lib/api";
import ProfilePictureUploader from "@/components/ProfilePictureUploader";
import PortfolioGallery from "@/components/PortfolioGallery";

const Card = ({ title, subtitle, accent = "#60a5fa", children, testId }) => (
  <section data-testid={testId} className="border border-zinc-800 bg-zinc-950/60 p-5">
    <div className="mb-4">
      <div className="text-[10px] tracking-[0.3em] uppercase font-mono" style={{ color: accent }}>{title}</div>
      {subtitle && <div className="text-[10px] font-mono text-zinc-300 mt-0.5">{subtitle}</div>}
    </div>
    {children}
  </section>
);

const Row = ({ label, help, children }) => (
  <div className="mb-3">
    <label className="text-[10px] tracking-[0.25em] uppercase text-zinc-200 block mb-1">{label}</label>
    {children}
    {help && <div className="mt-1 text-[10px] tracking-[0.15em] uppercase text-zinc-300 font-mono leading-relaxed">// {help}</div>}
  </div>
);

const Input = ({ testId, ...props }) => (
  <input data-testid={testId}
    className="w-full bg-black border border-zinc-800 focus:border-[#60a5fa] focus:outline-none text-white placeholder-zinc-300 rounded-none font-mono text-xs px-3 py-2.5"
    {...props} />
);

export default function SettingsPage() {
  const [prefs, setPrefs] = useState(null);
  const [connect, setConnect] = useState(null);
  const [ref, setRef] = useState(null);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState("");
  const [push, setPush] = useState({ supported: false, permission: "default", subscribed: false, endpoint: null });
  const [pushBusy, setPushBusy] = useState(false);
  const [me, setMe] = useState({ name: "", email: "", picture: "" });

  const refreshPushStatus = async () => {
    const supported = pushSupported();
    if (!supported) { setPush({ supported: false, permission: "denied", subscribed: false, endpoint: null }); return; }
    let subscribed = false, endpoint = null;
    try {
      const sub = await getCurrentSubscription();
      subscribed = !!sub;
      endpoint = sub?.endpoint || null;
    } catch (err) {
      devWarn("[settings] getCurrentSubscription failed:", err?.message || err);
    }
    setPush({ supported, permission: currentPermission(), subscribed, endpoint });
    // Best-effort re-sync with backend
    try {
      await pushStatus();
    } catch (err) {
      devWarn("[settings] pushStatus sync failed:", err?.message || err);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [p, c, r, u] = await Promise.all([getAlertPrefs(), connectStatus(), getMyReferral(), fetchMe()]);
        setPrefs(p);
        setConnect(c);
        setRef(r);
        setMe({ name: u?.name || "", email: u?.email || "", picture: u?.picture || "" });
      } catch (err) {
        toast.error("Could not load settings");
      }
    })();
    refreshPushStatus();
  }, []);

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (push.subscribed) {
        await disablePush();
        toast.success("Push notifications disabled");
      } else {
        await enablePush();
        toast.success("Push notifications enabled");
      }
      await refreshPushStatus();
    } catch (err) {
      toast.error(err?.message || "Could not toggle push");
    } finally {
      setPushBusy(false);
    }
  };

  const sendTestPush = async () => {
    try {
      await pushTest();
      toast.success("Test push sent — check your device");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Test push failed");
    }
  };

  const sendPushDigest = async () => {
    try {
      const r = await pushDigestSendNow();
      if (r.sent) toast.success(`Push digest sent: ${r.body}`);
      else toast.info(r.reason || "No activity to digest right now.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Push digest failed");
    }
  };

  if (!prefs) {
    return <div className="min-h-screen bg-black text-[#60a5fa] flex items-center justify-center font-mono text-sm tracking-widest uppercase">◇ Loading Settings ◇</div>;
  }

  const savePrefs = async () => {
    setSavingPrefs(true);
    try {
      const saved = await setAlertPrefs(prefs);
      setPrefs(saved);
      toast.success("Settings saved");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (Array.isArray(d) && d[0]?.msg) || "Save failed");
    } finally { setSavingPrefs(false); }
  };

  const startConnect = async () => {
    setBusy(true);
    try {
      const r = await connectOnboard();
      window.location.href = r.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Enable Stripe Connect on your Stripe dashboard first: dashboard.stripe.com/settings/connect");
      setBusy(false);
    }
  };

  const doExport = async () => {
    try {
      const data = await exportUserData();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `money-hub-export-${new Date().toISOString().slice(0, 10)}.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Data exported");
    } catch (err) { toast.error("Export failed"); }
  };

  const doDelete = async () => {
    if (confirmDelete !== "DELETE") { toast.error("Type DELETE to confirm"); return; }
    setBusy(true);
    try {
      await deleteUserAccount();
      toast.success("Account deleted");
      setTimeout(() => { window.location.href = "/"; }, 1200);
    } catch (err) { toast.error("Delete failed"); setBusy(false); }
  };

  const copyReferral = async () => {
    const url = `${window.location.origin}${ref?.share_url_template || ""}`;
    try { await navigator.clipboard.writeText(url); toast.success("Referral link copied"); }
    catch { toast.message(url); }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="sticky top-0 z-30 bg-black/95 backdrop-blur border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" data-testid="settings-back" className="font-mono text-[11px] tracking-[0.3em] uppercase text-zinc-200 hover:text-white">
            ← Dashboard
          </Link>
          <div className="font-heading text-lg font-black tracking-tight">Settings</div>
          <button data-testid="settings-save-top" onClick={savePrefs} disabled={savingPrefs}
            className="text-[10px] tracking-[0.3em] uppercase text-black bg-[#60a5fa] hover:bg-[#60a5fa] px-4 py-2 font-bold disabled:opacity-50">
            {savingPrefs ? "…" : "Save"}
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-6">

        {/* Profile — always visible, even in test-launch mode */}
        <Card testId="settings-profile" title="// profile" subtitle="Your avatar appears on the dashboard, marketplace cards, and referral leaderboard" accent="#60a5fa">
          <ProfilePictureUploader
            initialUrl={me.picture}
            name={me.name || me.email}
            onChange={(newPic) => setMe((prev) => ({ ...prev, picture: newPic }))}
          />
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs font-mono">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-300 mb-1">// name</div>
              <div className="text-white truncate">{me.name || "—"}</div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-300 mb-1">// email</div>
              <div className="text-white truncate">{me.email || "—"}</div>
            </div>
          </div>
        </Card>

        {/* Portfolio gallery — freelancer marketplace card */}
        <Card testId="settings-portfolio" title="// portfolio · marketplace card" subtitle="Before/after photos and gig samples clients see on your profile" accent="#22c55e">
          <PortfolioGallery />
        </Card>

        {/* Payments & Payouts — hidden during test launch */}
        {!TEST_LAUNCH_MODE && (
        <Card testId="settings-payouts" title="// payments & payouts · primary" subtitle="Link a bank to receive real payouts from clients" accent="#60a5fa">
          <div className="border border-zinc-800 p-4 mb-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-sm font-bold text-white">Stripe Connect · direct bank payouts</div>
                <div className="text-xs text-zinc-200 mt-1 leading-relaxed">
                  Link your bank via Stripe&apos;s onboarding (name, DOB, routing/account #). When clients pay
                  jobs through Money/Hub, Stripe automatically splits 80% to you, 20% platform fee — instantly, no manual invoicing.
                </div>
              </div>
              {connect?.payouts_enabled ? (
                <span className="shrink-0 text-[10px] tracking-[0.3em] uppercase text-[#22c55e] border border-[#22c55e]/50 px-3 py-2 whitespace-nowrap">✓ Connected</span>
              ) : connect?.connected ? (
                <button data-testid="connect-resume" onClick={startConnect} disabled={busy}
                  className="shrink-0 text-[10px] tracking-[0.3em] uppercase text-black bg-[#ffc439] hover:bg-[#ffb800] px-3 py-2 font-bold disabled:opacity-50 whitespace-nowrap">Finish onboarding →</button>
              ) : (
                <button data-testid="connect-start" onClick={startConnect} disabled={busy}
                  className="shrink-0 text-[10px] tracking-[0.3em] uppercase text-black bg-[#60a5fa] hover:bg-[#60a5fa] px-3 py-2 font-bold disabled:opacity-50 whitespace-nowrap">Link your bank →</button>
              )}
            </div>
          </div>

          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-200 mb-2">// or use direct-to-app deep links (secondary)</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Row label="Cash App $tag"><Input testId="s-cashtag" placeholder="yourtag" maxLength={20} value={prefs.cashtag || ""}
              onChange={(e) => setPrefs({ ...prefs, cashtag: e.target.value.replace(/^\$/, "").slice(0, 40) })} /></Row>
            <Row label="PayPal.me"><Input testId="s-paypal" placeholder="handle" value={prefs.paypal_me || ""}
              onChange={(e) => setPrefs({ ...prefs, paypal_me: e.target.value })} /></Row>
            <Row label="Venmo"><Input testId="s-venmo" placeholder="handle" value={prefs.venmo || ""}
              onChange={(e) => setPrefs({ ...prefs, venmo: e.target.value })} /></Row>
            <Row label="Zelle (email or phone)"><Input testId="s-zelle" placeholder="you@example.com" value={prefs.zelle || ""}
              onChange={(e) => setPrefs({ ...prefs, zelle: e.target.value })} /></Row>
          </div>
        </Card>
        )}

        {/* Test launch banner */}
        {TEST_LAUNCH_MODE && (
          <Card testId="settings-testlaunch-banner" title="// test launch · discovery mode" accent="#a855f7">
            <div className="text-sm text-zinc-200 leading-relaxed">
              MoneyHub is running in <b className="text-white">discovery &amp; planning mode</b> for this test launch.
              Marketplace payments, invoicing, Cash App deposits, and platform fees are turned off. Sign-in, alerts,
              privacy, terms, and refund policies remain available.
            </div>
            <div className="mt-3 text-xs text-zinc-300">
              Questions? Email <a href={`mailto:${SUPPORT_EMAIL}`} data-testid="support-email-link" className="text-[#60a5fa] hover:underline">{SUPPORT_EMAIL}</a>.
            </div>
          </Card>
        )}

        {/* Alerts */}
        <Card testId="settings-alerts" title="// alerts + daily digest" accent="#00F0FF">
          <Row label="Alternate email (verify to receive alerts here)">
            <div className="flex gap-2">
              <Input testId="s-alt-email" placeholder="you@example.com" value={prefs.alt_email || ""}
                onChange={(e) => setPrefs({ ...prefs, alt_email: e.target.value, alt_email_verified: false })} />
              <span className={`text-[10px] tracking-[0.3em] uppercase px-3 py-2.5 whitespace-nowrap ${prefs.alt_email_verified ? "text-[#22c55e] border border-[#22c55e]/50" : "text-zinc-300 border border-zinc-800"}`}>
                {prefs.alt_email_verified ? "✓ verified" : "unverified"}
              </span>
            </div>
          </Row>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
            {["enable_product", "enable_gig", "enable_crypto", "enable_task"].map((k) => (
              <label key={k} className="flex items-center gap-2 text-xs font-mono text-zinc-200">
                <input type="checkbox" checked={!!prefs[k]} onChange={(e) => setPrefs({ ...prefs, [k]: e.target.checked })} className="accent-[#60a5fa]" />
                {k.replace("enable_", "")}
              </label>
            ))}
          </div>
        </Card>

        {/* Push notifications */}
        <Card testId="settings-push" title="// push notifications · real-time + 6am digest" accent="#22c55e"
          subtitle="Real-time pings on job/invoice payments + a once-daily summary at your digest hour (default 7am ET).">
          {!push.supported ? (
            <div className="text-xs text-zinc-200 font-mono border border-zinc-800 p-3">
              // This browser doesn&apos;t support web push. On iOS, install Money/Hub to your home screen first (Safari → Share → Add to Home Screen).
            </div>
          ) : push.permission === "denied" ? (
            <div className="text-xs text-[#fb7185] font-mono border border-[#fb7185]/40 p-3">
              // Permission blocked at the browser level. Click the lock icon in the address bar → Site settings → Notifications → Allow, then reload.
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <button
                data-testid="push-toggle-btn"
                onClick={togglePush}
                disabled={pushBusy}
                className={`text-[10px] tracking-[0.3em] uppercase px-4 py-2.5 font-bold border transition-colors disabled:opacity-50 ${
                  push.subscribed
                    ? "text-[#22c55e] border-[#22c55e]/50 hover:bg-[#22c55e]/10"
                    : "text-black bg-[#22c55e] hover:bg-[#22c55e]/80 border-transparent"
                }`}
              >
                {(() => {
                  if (pushBusy) return "…";
                  return push.subscribed ? "Disable push" : "Enable push notifications";
                })()}
              </button>
              {push.subscribed && (
                <>
                  <button data-testid="push-test-btn" onClick={sendTestPush}
                    className="text-[10px] tracking-[0.3em] uppercase text-[#60a5fa] border border-[#60a5fa]/50 hover:bg-[#60a5fa]/10 px-3 py-2.5">
                    Send test →
                  </button>
                  <button data-testid="push-digest-now-btn" onClick={sendPushDigest}
                    className="text-[10px] tracking-[0.3em] uppercase text-[#ffc439] border border-[#ffc439]/50 hover:bg-[#ffc439]/10 px-3 py-2.5">
                    Send today&apos;s digest →
                  </button>
                </>
              )}
              <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-300 font-mono">
                {push.subscribed ? "✓ this device is subscribed" : "// not subscribed"}
              </span>
            </div>
          )}
        </Card>

        {/* Referral */}
        <Card testId="settings-referrals" title="// referrals" accent="#00F0FF">
          <div className="flex items-center justify-between gap-3 border border-zinc-800 p-3">
            <div className="min-w-0">
              <div className="font-mono text-xl text-[#00F0FF] tracking-wider">{ref?.code || "…"}</div>
              <div className="text-[10px] font-mono text-zinc-300 mt-1">{ref?.invited_count ?? 0} invited · {ref?.credits_remaining ?? 0} credits available</div>
            </div>
            <button data-testid="s-referral-copy" onClick={copyReferral}
              className="text-[10px] tracking-[0.3em] uppercase text-[#00F0FF] border border-[#00F0FF]/40 hover:bg-[#00F0FF]/10 px-3 py-2">Copy link</button>
          </div>
        </Card>

        {/* Privacy & data */}
        <Card testId="settings-privacy" title="// privacy & data" accent="#fb7185">
          <div className="flex flex-wrap gap-2">
            <Link to="/privacy" target="_blank" className="text-[10px] tracking-[0.3em] uppercase text-zinc-200 hover:text-white border border-zinc-800 px-3 py-2">Privacy policy →</Link>
            <Link to="/terms" target="_blank" className="text-[10px] tracking-[0.3em] uppercase text-zinc-200 hover:text-white border border-zinc-800 px-3 py-2">Terms →</Link>
            <button data-testid="s-export" onClick={doExport}
              className="text-[10px] tracking-[0.3em] uppercase text-[#60a5fa] hover:bg-[#60a5fa]/10 border border-[#60a5fa]/40 px-3 py-2">⇩ Export my data</button>
            <button data-testid="s-delete-open" onClick={() => setConfirmDelete("_open_")}
              className="text-[10px] tracking-[0.3em] uppercase text-[#fb7185] hover:bg-[#fb7185]/10 border border-[#fb7185]/40 px-3 py-2">Delete my account</button>
          </div>
          {confirmDelete === "_open_" && (
            <div className="mt-3 border border-[#fb7185]/40 bg-[#fb7185]/5 p-3">
              <div className="text-[10px] font-mono text-[#fb7185] mb-2">// PERMANENT · erases account, alerts, saved items, earnings, payments · type DELETE</div>
              <div className="flex flex-wrap items-center gap-2">
                <Input testId="s-delete-confirm-input" placeholder="DELETE" onChange={(e) => setConfirmDelete(e.target.value)} />
                <button data-testid="s-delete-confirm" onClick={doDelete} disabled={busy || confirmDelete !== "DELETE"}
                  className="text-[10px] tracking-[0.3em] uppercase text-black bg-[#fb7185] px-3 py-2 font-bold disabled:opacity-40">Confirm delete</button>
                <button onClick={() => setConfirmDelete("")} className="text-[10px] tracking-[0.3em] uppercase text-zinc-200 px-3 py-2">Cancel</button>
              </div>
            </div>
          )}
        </Card>

        <div className="pt-4 border-t border-zinc-800 flex justify-end">
          <button data-testid="settings-save-bottom" onClick={savePrefs} disabled={savingPrefs}
            className="text-[11px] tracking-[0.3em] uppercase text-black bg-[#60a5fa] hover:bg-[#60a5fa] px-6 py-3 font-bold disabled:opacity-50">
            {savingPrefs ? "Saving…" : "Save all changes →"}
          </button>
        </div>
      </main>
    </div>
  );
}
